
import * as THREE from "../../libs/three.js/build/three.module.js";

// Die Wuerfelflaechen werden zur Laufzeit auf ein Canvas gezeichnet statt aus PNGs geladen.
// So laesst sich der Wuerfel ueber setTheme() an das Seiten-Theme anpassen (MEASUREweb:
// site.js liest die Dataflor-CSS-Variablen und ruft setTheme beim Start und bei jedem
// Theme-Wechsel). Ohne setTheme entspricht die Darstellung den frueheren PNG-Texturen.
export class NavigationCube extends THREE.Object3D {

	constructor(viewer){
		super();

		this.viewer = viewer;

		this.theme = NavigationCube.defaultTheme;
		this.textureSize = 256;

		this.normalFaces = {};
		this.highlightedFaces = {};
		for (let face of ['U', 'D', 'L', 'R', 'F', 'B']) {
			this.normalFaces[face] = this.createFaceMaterial(face, false);
			this.highlightedFaces[face] = this.createFaceMaterial(face, true);
		}

		let planeGeometry = new THREE.PlaneGeometry(1, 1);

		this.front = new THREE.Mesh(planeGeometry, this.normalFaces["F"]);
		this.front.position.y = -0.5;
		this.front.rotation.x = Math.PI / 2.0;
		this.front.updateMatrixWorld();
		this.front.name = "F";
		this.add(this.front);

		this.back = new THREE.Mesh(planeGeometry, this.normalFaces["B"]);
		this.back.position.y = 0.5;
		this.back.rotation.x = Math.PI / 2.0;
		this.back.updateMatrixWorld();
		this.back.name = "B";
		this.add(this.back);

		this.left = new THREE.Mesh(planeGeometry, this.normalFaces["L"]);
		this.left.position.x = -0.5;
		this.left.rotation.y = Math.PI / 2.0;
		this.left.updateMatrixWorld();
		this.left.name = "L";
		this.add(this.left);

		this.right = new THREE.Mesh(planeGeometry, this.normalFaces["R"]);
		this.right.position.x = 0.5;
		this.right.rotation.y = Math.PI / 2.0;
		this.right.updateMatrixWorld();
		this.right.name = "R";
		this.add(this.right);

		this.bottom = new THREE.Mesh(planeGeometry, this.normalFaces["D"]);
		this.bottom.position.z = -0.5;
		this.bottom.updateMatrixWorld();
		this.bottom.name = "D";
		this.add(this.bottom);

		this.top = new THREE.Mesh(planeGeometry, this.normalFaces["U"]);
		this.top.position.z = 0.5;
		this.top.updateMatrixWorld();
		this.top.name = "U";
		this.add(this.top);

		this.width = 150; // in px
		this.yOffset = 70; // in px

		this.highlightedFace = "";

		this.camera = new THREE.OrthographicCamera(-1, 1, 1, -1, -1, 1);
		this.camera.position.copy(new THREE.Vector3(0, 0, 0));
		this.camera.lookAt(new THREE.Vector3(0, 1, 0));
		this.camera.updateMatrixWorld();
		this.camera.rotation.order = "ZXY";

		let onMouseDown = (event) => {
			if (!this.visible)
				return;
			let pickedFace = this.raycast(new THREE.Vector2(event.clientX, event.clientY), event.target);
			
			if (pickedFace)
				this.viewer.setView(pickedFace.name);
		};

		let onMouseMove = (event) => {
			if (!this.visible)
				return;

			let pickedFace = this.raycast(new THREE.Vector2(event.clientX, event.clientY), event.target);

			if (pickedFace) {
				if (pickedFace.name === this.highlightedFace)
					return;
				this.highlightFace(pickedFace.name);
			}
			else
				this.unhighlightFace(this.highlightedFace);
		};

		this.viewer.renderer.domElement.addEventListener('mousedown', onMouseDown, false);
		this.viewer.renderer.domElement.addEventListener('mousemove', onMouseMove, false);
	}

	// Farben und Beschriftungen ohne setTheme(): entsprechen den frueheren PNG-Texturen.
	static get defaultTheme() {
		return {
			face: '#373740',
			edge: '#AFAFB0',
			text: '#D9D9D9',
			highlightFace: '#555562',
			highlightText: '#D9D9D9',
			font: 'sans-serif',
			labels: {U: 'Oben', D: 'Unten', L: 'Links', R: 'Rechts', F: 'Vorne', B: 'Hinten'}
		};
	}

	// Die Ebenen liegen so im Wuerfel, dass ihre Textur von aussen teils gespiegelt oder
	// gedreht erscheint. Die Beschriftung wird deshalb je Flaeche vorverzerrt gezeichnet
	// (Canvas-Transformationsmatrix a, b, c, d, e, f). Oben und Unten sind so ausgerichtet,
	// dass sie richtig herum stehen, wenn man den Wuerfel aus der Vorderansicht nach oben
	// bzw. nach unten kippt - wie bei einem echten Wuerfel.
	static faceTransform(face, size) {
		switch (face) {
			case 'B': return [-1, 0, 0, 1, size, 0];     // horizontal gespiegelt
			case 'D': return [1, 0, 0, -1, 0, size];     // vertikal gespiegelt
			case 'R': return [0, -1, 1, 0, 0, size];     // 90 Grad gegen den Uhrzeigersinn
			case 'L': return [0, 1, 1, 0, 0, 0];         // an der Diagonale gespiegelt
			default: return [1, 0, 0, 1, 0, 0];          // F und U unveraendert
		}
	}

	createFaceMaterial(face, highlighted) {
		let canvas = document.createElement('canvas');
		canvas.width = this.textureSize;
		canvas.height = this.textureSize;

		let texture = new THREE.CanvasTexture(canvas);
		texture.anisotropy = this.viewer.renderer.capabilities.getMaxAnisotropy();

		let material = new THREE.MeshBasicMaterial({
			map: texture,
			depthTest: true,
			depthWrite: true,
			side: THREE.DoubleSide
		});
		material.userData.face = face;
		material.userData.highlighted = highlighted;

		this.drawFace(material);

		return material;
	}

	drawFace(material) {
		let {face, highlighted} = material.userData;
		let theme = this.theme;
		let canvas = material.map.image;
		let size = canvas.width;
		let ctx = canvas.getContext('2d');

		ctx.setTransform(1, 0, 0, 1, 0, 0);
		ctx.fillStyle = highlighted ? theme.highlightFace : theme.face;
		ctx.fillRect(0, 0, size, size);

		let border = size / 32;
		ctx.lineWidth = border;
		ctx.strokeStyle = theme.edge;
		ctx.strokeRect(border / 2, border / 2, size - border, size - border);

		// Beschriftung mittig; bei langen Woertern die Schrift so weit verkleinern, dass sie
		// in die Flaeche passt
		let label = theme.labels[face] || face;
		let fontSize = Math.round(size * 0.22);
		ctx.font = `${fontSize}px ${theme.font}`;
		let maxWidth = size - 4 * border;
		let width = ctx.measureText(label).width;
		if (width > maxWidth) {
			fontSize = Math.floor(fontSize * maxWidth / width);
			ctx.font = `${fontSize}px ${theme.font}`;
		}

		ctx.setTransform(...NavigationCube.faceTransform(face, size));
		ctx.fillStyle = highlighted ? theme.highlightText : theme.text;
		ctx.textAlign = 'center';
		ctx.textBaseline = 'middle';
		ctx.fillText(label, size / 2, size / 2);

		material.map.needsUpdate = true;
	}

	// Farben (beliebige CSS-Farbwerte), Schrift und Beschriftungen setzen; fehlende Werte
	// kommen aus defaultTheme. Zeichnet alle zwoelf Flaechen (normal und hervorgehoben) neu.
	setTheme(theme) {
		let defaults = NavigationCube.defaultTheme;
		this.theme = Object.assign({}, defaults, theme);
		this.theme.labels = Object.assign({}, defaults.labels, theme ? theme.labels : null);

		for (let face of Object.keys(this.normalFaces)) {
			this.drawFace(this.normalFaces[face]);
			this.drawFace(this.highlightedFaces[face]);
		}
	}

	highlightFace(face) {
		if (face === "" || this.highlightedFace === face)
			return;

		this.unhighlightFace(this.highlightedFace)

		let faceMesh = this.getFace(face);
		faceMesh.material = this.highlightedFaces[faceMesh.name];

		this.highlightedFace = face;
	}

	unhighlightFace(face) {
		if (this.highlightedFace === "" || face === "" || this.highlightedFace !== face)
			return;

		let faceMesh = this.getFace(face);
		faceMesh.material = this.normalFaces[faceMesh.name];

		this.highlightedFace = "";
    }

	getFace(faceName) {
		return this.children.find(f => f.name === faceName);
    }

	update(rotation) {
		this.camera.rotation.copy(rotation);
		this.camera.updateMatrixWorld();
	}

	raycast(clickPoint, clientCanvas) {
		let viewportOffset = clientCanvas.getBoundingClientRect();
		let mouse = new THREE.Vector2();
		mouse.x = (clickPoint.x - viewportOffset.x) - (clientCanvas.clientWidth - this.width);
		mouse.y = (clickPoint.y - viewportOffset.y) - this.yOffset;

		if (mouse.x < 0 || mouse.y > this.width) return null;

		mouse.x = (mouse.x / this.width) * 2 - 1;
		mouse.y = -(mouse.y / this.width) * 2 + 1;

		let raycaster = new THREE.Raycaster();
		raycaster.setFromCamera(mouse, this.camera);
		raycaster.ray.origin.sub(this.camera.getWorldDirection(new THREE.Vector3()));

		let intersects = raycaster.intersectObjects(this.children);

		var pickedFace = null;
		let minDistance = 1000;
		for (let i = 0; i < intersects.length; i++) {
			if (intersects[i].distance < minDistance) {
				pickedFace = intersects[i].object;
				minDistance = intersects[i].distance;
			}
		}

		return pickedFace;
    }
}
